import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid2, useMediaQuery } from '@mui/material';
import { useRootStore } from '../../stores';
import { useNavigate } from 'react-router';
import { ERoutes } from '../../enums';
import { ScreenWrapper } from '../../components/Wrappers/ScreenWrapper';
import theme, { TMaterialStyles } from '../../theme';
import { useTranslation } from 'react-i18next';
import TypographyWithShadow from '../../components/Typography/TypographyWithShadow';
import { Illustration } from '../../components/Illustration';
import Image from '../../components/Common/Image';
import { Icon } from '../../components/Icon';
import { DateTime } from 'luxon';
import { addSuiAddress, getUserStats } from '../../queries';
import { formatNumberWithCommas } from '../../helpers';
import { ButtonWithShadow } from '../../components/Buttons/ButtonWithShadow';
import { ConfirmModalWithOutsideButton } from '../../components/Modals/ConfirmModalWithOutsideButton';
import { SUI_ADDRESS_REGEXP } from '../../api';
import { customToast } from '../../utils';
import { TextArea } from '../../components/Common/TextArea';

const UserProfileScreen: React.FC = () => {
  const { t } = useTranslation();
  const { user, userStats } = useRootStore();
  const [isConfirmChangeWalletModalOpen, setIsConfirmChangeWalletModalOpen] =
    useState(false);

  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [walletAddress, setWalletAddress] = useState<string>(
    user && user.wallet_address ? user.wallet_address : '',
  );

  const navigate = useNavigate();

  const isAddOrChangeWalletDisabled = useMemo(() => {
    if (user?.wallet_address === walletAddress) {
      return true;
    }

    return !SUI_ADDRESS_REGEXP.test(walletAddress);
  }, [user?.wallet_address, walletAddress]);

  const isAvailableChangeOrSetWalletAddress = useMemo(() => {
    if (!user) {
      return false;
    }

    return user.wallet_update_count < 3;
  }, [user]);

  const parsedUserCreatedAt = useMemo(() => {
    if (!user?.created_at) {
      return;
    }

    return DateTime.fromISO(user.created_at)
      .setZone('UTC')
      .toFormat('dd/MM/yyyy');
  }, [user?.created_at]);

  useEffect(() => {
    getUserStats();
  }, []);

  const onBack = useCallback(() => navigate(ERoutes.main), [navigate]);

  const onOpenConfirmChangeWalletModal = useCallback(() => {
    if (isAddOrChangeWalletDisabled) {
      customToast(
        {
          title: t('user_profile.sui_wallet_address_is_incorrect'),
          subtitle: t('user_profile.try_another_one'),
        },
        'info',
      );
      return;
    }
    setIsConfirmChangeWalletModalOpen(true);
  }, [isAddOrChangeWalletDisabled, t]);

  const onCloseConfirmChangeWalletModal = useCallback(
    () => setIsConfirmChangeWalletModalOpen(false),
    [],
  );

  const onChangeWalletAddress = useCallback((event: any) => {
    const { value } = event.target;

    setWalletAddress(value);
  }, []);

  const onAddSuiAddress = useCallback(async () => {
    try {
      await addSuiAddress(walletAddress);

      customToast(
        {
          title: t('user_profile.wallet_success_changed'),
          subtitle: t('user_profile.you_have_two_more_attempts', {
            value: 3 - Number(user?.wallet_update_count),
          }),
        },
        'success',
      );

      onCloseConfirmChangeWalletModal();
    } catch (error: any) {
      console.error('Error while [onAddSuiAddress]', error);
    }
  }, [
    onCloseConfirmChangeWalletModal,
    t,
    user?.wallet_update_count,
    walletAddress,
  ]);

  return (
    <ScreenWrapper onBack={onBack}>
      <Grid2 sx={materialStyles.screenContainer}>
        <Box sx={materialStyles.statsAndUserProfileContainer}>
          <Box sx={[materialStyles.blockContainer, { flex: 1 }]}>
            <Box sx={materialStyles.blockTitleContainer}>
              <TypographyWithShadow
                sx={materialStyles.blockTitle}
                shadowType="DARK"
              >
                {t('user_profile.user_profile')}
              </TypographyWithShadow>
            </Box>
            <Box sx={materialStyles.userProfileContainer}>
              <Box sx={materialStyles.avatarContainer}>
                <Illustration
                  sx={materialStyles.avatarIllustration}
                  illustrationName="UserAvatarBorder"
                />
                <Icon iconName="UserIcon" sx={materialStyles.avatarEmptyIcon} />
                {user?.avatar_url ? (
                  <Image
                    sx={materialStyles.userAvatar}
                    src={user?.avatar_url}
                  />
                ) : null}
              </Box>
              <Box sx={materialStyles.userProfileInfoContainer}>
                <TypographyWithShadow
                  shadowType="DARK"
                  sx={materialStyles.usernameTitle}
                >
                  {user?.username}
                </TypographyWithShadow>
                <Box sx={materialStyles.userProfileInfoContainerRow}>
                  <Icon
                    iconName="GoldIdIcon"
                    sx={materialStyles.userProfileInfoRowIcon}
                  />
                  <TypographyWithShadow
                    shadowType="DARK"
                    sx={materialStyles.userProfileInfoRowTitle}
                  >
                    {user?.tg_id}
                  </TypographyWithShadow>
                </Box>
                <Box sx={materialStyles.userProfileInfoContainerRow}>
                  <Icon
                    iconName="GoldCalendarIcon"
                    sx={materialStyles.userProfileInfoRowIcon}
                  />
                  <TypographyWithShadow
                    shadowType="DARK"
                    sx={materialStyles.userProfileInfoRowTitle}
                  >
                    {parsedUserCreatedAt}
                  </TypographyWithShadow>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={[
              materialStyles.blockContainer,
              materialStyles.walletBlockContainer,
            ]}
          >
            <Box sx={materialStyles.blockTitleContainer}>
              <Icon iconName="WalletIcon" sx={materialStyles.walletIcon} />
              <TypographyWithShadow
                sx={materialStyles.blockTitle}
                shadowType="DARK"
              >
                {t('user_profile.wallet')}
              </TypographyWithShadow>
            </Box>
            <Box sx={materialStyles.walletContainer}>
              <TextArea
                value={walletAddress || ''}
                placeholder={t('user_profile.enter_your_sui_wallet_there')}
                onChange={onChangeWalletAddress}
                readOnly={!isAvailableChangeOrSetWalletAddress}
                disabled={!isAvailableChangeOrSetWalletAddress}
                rows={isDesktop ? 3 : 1}
              />
              {isAvailableChangeOrSetWalletAddress ? (
                <Box sx={materialStyles.walletButtonsContainer}>
                  <ButtonWithShadow
                    sx={materialStyles.walletButton}
                    type={isAddOrChangeWalletDisabled ? 'DARK' : 'PRIMARY_DARK'}
                    onClick={onOpenConfirmChangeWalletModal}
                  >
                    {user?.wallet_address
                      ? t('common.change')
                      : t('common.submit')}
                  </ButtonWithShadow>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
        <Box sx={materialStyles.blockContainer}>
          <Box sx={materialStyles.blockTitleContainer}>
            <TypographyWithShadow
              sx={materialStyles.blockTitle}
              shadowType="DARK"
            >
              {t('user_profile.stats')}
            </TypographyWithShadow>
          </Box>
          <Box sx={materialStyles.statsContainer}>
            <Box sx={materialStyles.statsContainerRow}>
              <Box sx={materialStyles.statContainerWithSubContainer}>
                <Box sx={materialStyles.statContainer}>
                  <Icon
                    iconName="TotalPointsIcon"
                    sx={materialStyles.totalPointsIcon}
                  />
                  <TypographyWithShadow
                    shadowType="NONE"
                    sx={materialStyles.smallStatTitle}
                  >
                    {t('user_profile.general_points')}
                  </TypographyWithShadow>
                  <Box sx={materialStyles.statValueContainer}>
                    <TypographyWithShadow
                      shadowType="NONE"
                      sx={materialStyles.statValueText}
                    >
                      {formatNumberWithCommas(userStats?.general_points) || '0'}
                    </TypographyWithShadow>
                  </Box>
                </Box>
                <Box sx={materialStyles.statContainer}>
                  <TypographyWithShadow
                    shadowType="NONE"
                    sx={materialStyles.smallStatTitle}
                  >
                    {t('user_profile.competition_points')}
                  </TypographyWithShadow>
                  <Box sx={materialStyles.statValueContainer}>
                    <TypographyWithShadow
                      shadowType="NONE"
                      sx={materialStyles.statValueTextWithGradient}
                    >
                      {formatNumberWithCommas(userStats?.competition_points) ||
                        '0'}
                    </TypographyWithShadow>
                  </Box>
                </Box>
              </Box>
              <Box sx={materialStyles.statContainerWithSubContainer}>
                <Box sx={materialStyles.statContainer}>
                  <Icon
                    iconName="ReferralsIcon"
                    sx={materialStyles.referralsIcon}
                  />
                  <TypographyWithShadow
                    shadowType="NONE"
                    sx={materialStyles.smallStatTitle}
                  >
                    {t('user_profile.referrals')}
                  </TypographyWithShadow>
                  <Box sx={materialStyles.statValueContainer}>
                    <TypographyWithShadow
                      shadowType="NONE"
                      sx={materialStyles.statValueText}
                    >
                      {userStats?.referral_count || '0'}
                    </TypographyWithShadow>
                  </Box>
                </Box>
                <Box sx={materialStyles.statContainer}>
                  <TypographyWithShadow
                    shadowType="NONE"
                    sx={materialStyles.smallStatTitle}
                  >
                    {t('user_profile.referral_earnings')}
                  </TypographyWithShadow>
                  <Box sx={materialStyles.statValueContainer}>
                    <TypographyWithShadow
                      shadowType="NONE"
                      sx={materialStyles.statValueText}
                    >
                      {formatNumberWithCommas(userStats?.referral_points) ||
                        '0'}
                    </TypographyWithShadow>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={materialStyles.statsRankContainerRow}>
              <Box sx={materialStyles.statContainer}>
                <Icon
                  iconName="LeaderBoardPositionIcon"
                  sx={materialStyles.leaderboardPositionIcon}
                />
                <TypographyWithShadow
                  shadowType="NONE"
                  sx={materialStyles.statTitle}
                >
                  {t('user_profile.competition_position')}
                </TypographyWithShadow>
                <Box sx={materialStyles.statValueContainer}>
                  <TypographyWithShadow
                    shadowType="NONE"
                    sx={materialStyles.statValueText}
                  >
                    {userStats?.weekly_leaderboard_position || '?'}
                  </TypographyWithShadow>
                </Box>
              </Box>
              <Box sx={materialStyles.statContainer}>
                <Icon
                  iconName="TotalPositionIcon"
                  sx={materialStyles.totalPositionIcon}
                />
                <TypographyWithShadow
                  shadowType="NONE"
                  sx={materialStyles.statTitle}
                >
                  {t('user_profile.general_position')}
                </TypographyWithShadow>
                <Box sx={materialStyles.statValueContainer}>
                  <TypographyWithShadow
                    shadowType="NONE"
                    sx={materialStyles.statValueText}
                  >
                    {userStats?.total_leaderboard_position || '?'}
                  </TypographyWithShadow>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid2>
      <ConfirmModalWithOutsideButton
        onClose={onCloseConfirmChangeWalletModal}
        onSubmit={onAddSuiAddress}
        isOpen={isConfirmChangeWalletModalOpen}
        title={t('user_profile.you_can_change_your_wallet_up_to_times', {
          value: 3 - Number(user?.wallet_update_count),
        })}
        subtitle={t(
          'user_profile.after_reaching_limit_further_changes_will_disabled',
        )}
        closeButtonTitle={t('common.back')}
        submitButtonTitle={t('common.confirm')}
      />
    </ScreenWrapper>
  );
};

const materialStyles: TMaterialStyles = {
  screenContainer: {
    flex: 1,
    paddingX: '18px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    pb: '24px',
    maxWidth: '738px',
    width: '100%',
    margin: 'auto',
  },
  statsAndUserProfileContainer: {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      lg: 'row',
    },
    gap: {
      xs: '0px',
      lg: '27px',
    },
    width: '100%',
  },
  blockTitle: {
    fontFamily: 'Rubber Duck !important',
    color: 'white',
    textAlign: 'center',
    fontSize: '35px',
    lineHeight: '100%',
    textTransform: 'uppercase',
  },
  blockContainer: {
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0px 4px 0px ${theme.palette.secondary.main}`,
    borderRadius: '14px',
    marginTop: {
      xs: '10px',
      lg: '27px',
    },
    display: 'flex',
    flexDirection: 'column',
    overflow: {
      xs: undefined,
      lg: 'hidden',
    },
  },
  walletBlockContainer: {
    minWidth: {
      xs: undefined,
      lg: '340px',
    },
  },
  blockTitleContainer: {
    py: '15px',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: `0px 4px 0px ${theme.palette.secondary.dark}`,
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  userProfileContainer: {
    display: 'flex',
    px: '21px',
    py: '17px',
    flexDirection: 'row',
    flex: 1,
    overflow: 'auto',
  },
  avatarContainer: {
    width: '110px',
    height: '110px',
    position: 'relative',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[50],
  },
  avatarEmptyIcon: {
    width: '25px',
    height: '30px',
  },
  avatarIllustration: {
    width: '110px',
    height: '110px',
    position: 'absolute',
    zIndex: 1,
  },
  userAvatar: {
    width: '110px',
    height: '110px',
    borderRadius: '20px',
    position: 'absolute',
    zIndex: 0,
  },
  userProfileInfoContainer: {
    ml: '13px',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  usernameTitle: {
    fontFamily: 'Rubber Duck !important',
    textTransform: 'uppercase',
    fontSize: '35px',
    lineHeight: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flex: 1,
    whiteSpace: 'nowrap',
  },
  userProfileInfoContainerRow: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '8px',
    marginTop: '10px',
  },
  userProfileInfoRowIcon: {
    width: '24px',
    height: '24px',
  },
  userProfileInfoRowTitle: {
    fontFamily: 'Rubber Duck !important',
    textTransform: 'uppercase',
    fontSize: '22px',
    lineHeight: '100%',
  },
  statsContainer: {
    my: '16px',
    mx: '7px',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: `0px 4px 0px ${theme.palette.secondary.dark}`,
    p: '11px',
    borderRadius: '14px',
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' },
    alignItems: { xs: undefined, lg: 'flex-end' },
    gap: '21px',
  },
  statsContainerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '20px',
    flex: {
      xs: 0,
      lg: 1,
    },
  },
  statsRankContainerRow: {
    display: 'flex',
    flexDirection: { xs: 'row', lg: 'column' },
    gap: { xs: '20px', lg: '0' },
    flex: { sx: 0, lg: 0.5 },
  },
  statContainerWithSubContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  statContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  totalPointsIcon: {
    width: '58px',
    height: '34px',
  },
  statTitle: {
    fontFamily: 'Rubber Duck !important',
    fontSize: { xs: '12px', sm: '16px', md: '19px', lg: '19px' },
    lineHeight: '100%',
    marginTop: '10px',
    textAlign: 'center',
  },
  smallStatTitle: {
    fontFamily: 'Rubber Duck !important',
    fontSize: {
      xs: '12px',
      sm: '14px',
      md: '16px',
      lg: '19px',
    },
    lineHeight: '100%',
    marginTop: '10px',
    textAlign: 'center',
  },
  statValueContainer: {
    py: '3px',
    px: '9px',
    backgroundColor: theme.palette.secondary.dark,
    maxWidth: { xs: '149px', lg: '100%' },
    width: '100%',
    borderRadius: '8px',
    marginTop: '3px',
  },
  statValueText: {
    fontFamily: 'Rubber Duck !important',
    fontSize: '22px',
    lineHeight: '100%',
    textAlign: 'center',
  },
  statValueTextWithGradient: {
    fontFamily: 'Rubber Duck !important',
    fontSize: '22px',
    lineHeight: '100%',
    textAlign: 'center',
    background: theme.palette.utils.gradients.gold,
    '-webkit-background-clip': 'text',
    '-webkit-text-fill-color': 'transparent',
  },
  referralsIcon: {
    width: '60px',
    height: '54px',
  },
  leaderboardPositionIcon: {
    width: '53px',
    height: '45px',
  },
  totalPositionIcon: {
    width: '62px',
    height: '48px',
    display: {
      xs: 'block',
      lg: 'none',
    },
  },
  walletContainer: {
    pb: '14px',
    pt: '12px',
    px: '8px',
    display: 'flex',
    flexDirection: {
      xs: 'row',
      lg: 'column',
    },
    alignItems: 'center',
    gap: '7px',
  },
  walletButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  walletButton: {
    width: '93px',
    paddingTop: '12px',
    paddingBottom: '10px',
    borderRadius: '12px',
    fontFamily: 'Rubber Duck !important',
    textTransform: 'uppercase',
    fontSize: '14px',
  },
  walletIcon: {
    width: '39px',
    height: '38px',
  },
};

export default UserProfileScreen;
